import { useForm } from "react-hook-form";
import { loginService, toSession } from "../services/authenticationService";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuthContext } from "../context/AuthProvider";
import { homeFor } from "../utils/access";
import logo from "../assets/img/logo.jpg";
import "../assets/style/auth.css";
import Loader from "../components/shared/Loader";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useState } from "react";

export default function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthContext();

    // Destino solicitado antes de exigir sesión (perfil de modelo, sala en vivo...).
    const next = new URLSearchParams(location.search).get('next');

    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    useDocumentMeta({ title: "Iniciar sesión", noIndex: true });
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm();

    const onSubmit = async (body) => {
        setLoading(true);
        setServerError('');
        try {
            const result = await loginService(body);
            if (result?.user) {
                login(toSession(result));

                if (next) {
                    navigate(next, { replace: true });
                    return;
                }

                // Cada rol aterriza donde puede trabajar: la modelo en su
                // panel y no en un listado de salas desde el que no llegaba a
                // su propio perfil. Se decide por permisos, no por rol.
                navigate(homeFor(toSession(result)), { replace: true });
            } else {
                setServerError('No fue posible iniciar sesión. Intenta nuevamente.');
            }
        } catch (error) {
            // Sin este manejo el formulario quedaba mudo ante credenciales inválidas.
            setServerError(error?.message || 'Correo o contraseña incorrectos.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Loader loading={loading} message="Iniciando sesión" />
            <Link to="/" className="back-home">
                <i className="fas fa-arrow-left"></i> Volver al Inicio
            </Link>
            
            <div className="auth-card">
                <div className="auth-header">
                    <img src={logo} alt="Afrodita Logo" className="auth-logo" />
                    <h2 className="auth-title">Bienvenido</h2>
                    <p className="auth-subtitle">Ingresa tus credenciales para continuar</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
                    {serverError && (
                        <div className="alert alert-danger py-2 small" role="alert">
                            <i className="fas fa-circle-exclamation me-2"></i>{serverError}
                        </div>
                    )}
                    <div className="form-group mb-3">
                        <label className="form-label">Correo Electrónico</label>
                        <div className="auth-input-group">
                            <i className="fas fa-envelope"></i>
                            <input 
                                type="email" 
                                {...register("email", { required: true })}
                                className="auth-input" 
                                placeholder="tu@email.com" 
                            />
                        </div>
                        {errors.email && <small className="text-danger d-block mb-2">Este campo es requerido</small>}
                    </div>

                    <div className="form-group mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Contraseña</label>
                            <Link to="/" className="auth-link small" style={{fontSize: '0.75rem'}}>¿Olvidaste tu contraseña?</Link>
                        </div>
                        <div className="auth-input-group">
                            <i className="fas fa-lock"></i>
                            <input 
                                type="password" 
                                {...register("password", { required: true })}
                                className="auth-input" 
                                placeholder="••••••••" 
                            />
                        </div>
                        {errors.password && <small className="text-danger d-block mb-2">Este campo es requerido</small>}
                    </div>

                    <button type="submit" className="auth-btn" disabled={!isValid}>
                        Entrar ahora
                    </button>

                    <div className="auth-footer">
                        <span>¿Aún no tienes cuenta?</span>
                        <Link
                            to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}
                            className="auth-link ms-2"
                        >
                            Regístrate
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}